/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2021 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 *
 * @file ResourceListView.jsx
 * @author Srinivasan Bakthavatchalam
 * @since 1.0.1
 */

import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';

import Collapse from '@material-ui/core/Collapse';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import { withStyles } from '@material-ui/core/styles';
import { Icon } from '@greenville/mui-theme';

import ResourceDataComponent from '../ResourceDataComponent';
import ResourceUtils from '../../utils/ResourceUtils';
import StatusIcon from '../common/StatusIcon';
import {
  CONTENT_TITLE_OTHER_ICON,
  CONTENT_TYPE_KEYS,
  DOWNLOAD_STATUS_COMPLETED,
  DOWNLOAD_STATUS_ERROR,
  PAGE_LEVEL_RESOURCE
} from '../../common/constants';

const styles = (theme) => ({
  expandMoreOrLess: {
    color: theme.color.activePrimary
  },
  listItem: {
    borderBottom: `1px solid ${theme.color.divider}`
  },
  resourceDownloadCheckBox: {
    minWidth: 32
  },
  selectedSpan: {
    color: theme.color.activePrimary,
    paddingRight: 10
  }
});
const addIndent = (level) => ({
  paddingLeft: `${level * 10}px`
});
class ResourceListView extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      open: false
    };
  }

  componentDidMount() {
    const { isLastItem } = this.props;

    if (isLastItem) {
      ResourceUtils.handleScroll(this.props.currentPageId);
    }
  }

  componentDidUpdate(prevProps) {
    const { currentPageId } = prevProps;
    const { isLastItem, resourceLevelTabValue } = this.props;

    if (isLastItem && currentPageId.join() !== this.props.currentPageId.join()
    && resourceLevelTabValue === PAGE_LEVEL_RESOURCE) {
      ResourceUtils.handleScroll(this.props.currentPageId);
    }
  }

  /**
   * Handles resource item click
   *
   * @param {*} event
   * @param {*} resourceItem
   */
  handleClick = (event, resourceItem) => {
    const { onResourceClick } = this.props;

    if (resourceItem.subtopic) {
      const { open } = this.state;
      this.setState({
        open: !open
      });
    } else {
      onResourceClick(resourceItem);
    }
  };

  /**
   * Handles select checkbox click
   *
   * @param {*} event
   * @param {*} resourceItem
   */
  handleSelectClick = (event, resourceItem) => {
    const { selectedResources, setSelectedResources, isSelected } = this.props;

    const resourceSelected = !isSelected;
    if (resourceItem.subtopic) {
      const allChildrenResource = ResourceUtils.constructFlatData(resourceItem.subtopic);

      allChildrenResource.forEach(resource => {
        const foundResource = selectedResources.some(selectedResource => selectedResource.id === resource.id);

        if (ResourceUtils.isResourceDownloadable(resource)
            && ((resourceSelected && !foundResource)
                || (!resourceSelected && foundResource))
        ) {
          setSelectedResources(resource, false, true, isSelected);
        }
      });
    } else {
      setSelectedResources(resourceItem);
    }
    event.stopPropagation();
  };

  /**
   * Adds intent the child element
   * @param {*} level
   * @returns
   */
  // addIndent = (level) => ({
  //   paddingLeft: `${(level * 10)}px`
  // });

  /**
   * Render the resource items as list
   * @param {*} resourceItem
   * @param {*} indexVal
   * @param {*} level
   * @returns
   */
  renderResourceList = (resourceItem, indexVal, level) => {
    const { open } = this.state;
    const {
      bulkDownloadStatus,
      classes,
      isFilterDropDownOpen,
      isSelected,
      resourceLevelTabValue,
      showDownload
    } = this.props;
    const expandIcon = open ?
      <ExpandLess className={classes.expandMoreOrLess} /> :
      <ExpandMore className={classes.expandMoreOrLess} />;
    const contentTypeDetails = ResourceUtils.getContentTypeDetails(resourceItem.contentTypeValue,
      CONTENT_TYPE_KEYS.TYPE);
    const displayIcon =
      (<Icon
        color="inherit"
        name={contentTypeDetails
          && contentTypeDetails[0] ? contentTypeDetails[0][CONTENT_TYPE_KEYS.ICON] : CONTENT_TITLE_OTHER_ICON}
        size="small"
      />);
    const isDownloadable = (resourceItem.subtopic
      ? ResourceUtils.isParentResourceDownloadable(resourceItem.subtopic) :
      ResourceUtils.isResourceDownloadable(resourceItem));
    let groupDownloadedresourceStatus = false;
    if (resourceItem && resourceItem.subtopic) {
      const ChildrenResource = ResourceUtils.constructFlatData(
        resourceItem.subtopic
      );
      const status = ChildrenResource.every(
        (x) => x.status === DOWNLOAD_STATUS_COMPLETED
      );
      if (status) {
        groupDownloadedresourceStatus = true;
      }
    }

    return (
      <ListItem
        disabled={
          isFilterDropDownOpen ||
          ResourceUtils.isBulkDownloadInProgress(bulkDownloadStatus)
        }
        className={`resource-list ${
          level === 1 ? classes.listItem : ''
        } pageId-${resourceItem.pageId}`}
        button
        id={`${resourceItem.id || indexVal}`}
        onClick={(event) => this.handleClick(event, resourceItem)}
        style={addIndent(level)}
      >
        {showDownload && (
          <ListItemIcon className={classes.resourceDownloadCheckBox}>
            {isDownloadable && (
              <StatusIcon
                bulkDownloadStatus={bulkDownloadStatus}
                selected={isSelected}
                isFilterDropDownOpen={isFilterDropDownOpen}
                handleClick={(event) => this.handleSelectClick(event, resourceItem)
                }
                resource={resourceItem}
                status={resourceItem.status}
              />
            )}
          </ListItemIcon>
        )}
        <ListItemText
          primary={resourceItem.name}
          secondary={
            resourceLevelTabValue === PAGE_LEVEL_RESOURCE &&
            `Page ${resourceItem.pageId}`
          }
          className={`resource-item-text-${level === 1 ? 'bold' : 'normal'}`}
        />
        {showDownload &&
          resourceItem.subtopic &&
          groupDownloadedresourceStatus && (
            <span className={classes.selectedSpan}>
              <Icon color="inherit" name="Right" size="small" />
            </span>
        )}
        {(resourceItem.status === DOWNLOAD_STATUS_COMPLETED ||
          resourceItem.status === DOWNLOAD_STATUS_ERROR) &&
          !isSelected && (
            <span className={classes.selectedSpan}>
              <Icon
                color={
                  resourceItem.status === DOWNLOAD_STATUS_COMPLETED
                    ? 'inherit'
                    : 'error'
                }
                name={
                  resourceItem.status === DOWNLOAD_STATUS_COMPLETED
                    ? 'Right'
                    : 'Warning'
                }
                size="small"
              />
            </span>
        )}
        {resourceItem.subtopic ? expandIcon : displayIcon}
      </ListItem>
    );
  };

  render() {
    const {
      bulkDownloadStatus,
      currentPageId,
      indexVal,
      isFilterDropDownOpen,
      isSearchAndFilterOff,
      level,
      onResourceClick,
      resourceItem,
      resourceLevelTabValue,
      selectedResources,
      setSelectedResources,
      showDownload,
      userRoleTabValue
    } = this.props;
    const { open } = this.state;

    return (
      <Fragment>
        {this.renderResourceList(resourceItem, indexVal, level)}
        {resourceItem.subtopic &&
        <Collapse in={open} timeout="auto" unmountOnExit>
          <ResourceDataComponent
            bulkDownloadStatus={bulkDownloadStatus}
            currentPageId={currentPageId}
            isCardView={false}
            isFilterDropDownOpen={isFilterDropDownOpen}
            isSearchAndFilterOff={isSearchAndFilterOff}
            level={level + 1}
            onResourceClick={onResourceClick}
            resourceData={resourceItem.subtopic}
            resourceLevelTabValue={resourceLevelTabValue}
            selectedResources={selectedResources}
            setSelectedResources={setSelectedResources}
            showDownload={showDownload}
            userRoleTabValue={userRoleTabValue}
          />
        </Collapse>
        }
      </Fragment>
    );
  }
}

ResourceListView.propTypes = {
  bulkDownloadStatus: PropTypes.string.isRequired,
  classes: PropTypes.object.isRequired,
  currentPageId: PropTypes.array.isRequired,
  indexVal: PropTypes.number.isRequired,
  isFilterDropDownOpen: PropTypes.bool.isRequired,
  isLastItem: PropTypes.bool.isRequired,
  isSearchAndFilterOff: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool,
  level: PropTypes.number,
  onResourceClick: PropTypes.func.isRequired,
  resourceItem: PropTypes.object,
  resourceLevelTabValue: PropTypes.string.isRequired,
  selectedResources: PropTypes.array.isRequired,
  setSelectedResources: PropTypes.func.isRequired,
  showDownload: PropTypes.bool.isRequired,
  userRoleTabValue: PropTypes.string.isRequired
};
ResourceListView.defaultProps = {
  isSelected: false,
  level: 1,
  resourceItem: {}
};
export default withStyles(styles)(ResourceListView);
